import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@/contexts/AuthContext';
import { fetchProjectsFromDb } from '@/hooks/useProjects';
import { fetchTasksFromDb } from '@/hooks/useTasks';
import { fetchMembersFromDb } from '@/hooks/useTeamMembers';
import { fetchBookingsFromDb } from '@/hooks/useBookings';
import { fetchContactsFromDb } from '@/hooks/useContacts';
import { fetchMarketingDataFromDb } from '@/hooks/useMarketing';
import { fetchCalendarEventsFromDb } from '@/hooks/useCalendarEvents';
import { fetchInsurancesFromDb } from '@/hooks/useInsurance';
import { fetchEmailAccountsFromDb } from '@/hooks/useEmailAccounts';

export const useDashboardPrefetch = () => {
  const queryClient = useQueryClient();
  const { user, initialized } = useAuthContext();

  useEffect(() => {
    if (!user || !initialized) return;

    const warmDashboardData = async () => {
      try {
        await Promise.allSettled([
          queryClient.prefetchQuery({ queryKey: ['projects'], queryFn: fetchProjectsFromDb, staleTime: 60_000 }),
          queryClient.prefetchQuery({ queryKey: ['tasks'], queryFn: () => fetchTasksFromDb(), staleTime: 60_000 }),
          queryClient.prefetchQuery({ queryKey: ['team-members'], queryFn: fetchMembersFromDb, staleTime: 60_000 }),
          queryClient.prefetchQuery({ queryKey: ['bookings'], queryFn: fetchBookingsFromDb, staleTime: 60_000 }),
          queryClient.prefetchQuery({ queryKey: ['contacts'], queryFn: fetchContactsFromDb, staleTime: 60_000 }),
          queryClient.prefetchQuery({ queryKey: ['marketing'], queryFn: fetchMarketingDataFromDb, staleTime: 60_000 }),
          queryClient.prefetchQuery({ queryKey: ['calendar-events', undefined, undefined], queryFn: () => fetchCalendarEventsFromDb(), staleTime: 60_000 }),
          queryClient.prefetchQuery({ queryKey: ['insurances', 'all'], queryFn: () => fetchInsurancesFromDb(), staleTime: 60_000 }),
          queryClient.prefetchQuery({ queryKey: ['email-accounts'], queryFn: fetchEmailAccountsFromDb, staleTime: 60_000 }),
        ]);
      } catch {
        // Silently ignore prefetch errors
      }
    };

    const timer = window.setTimeout(() => {
      void warmDashboardData();
    }, 250);

    return () => window.clearTimeout(timer);
  }, [initialized, queryClient, user]);
};